import { TABLE_COLUMNS, processResponse } from 'components/LM/Voice/Search/VoiceSearch';
import AdvancedTable from 'components/LM/common/AdvancedTable';
import ExportCSV from "components/LM/common/ExportCSV";
import K2Api from 'lib/ajax/K2Api';
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import { useHistory } from 'react-router-dom';


import { Loader, TablePagination } from '@launchmetrics/lm-ui';
import GoTopButton from 'components/LM/common/GoTopButton';
import './voicemsearch.scss';

const CSV_COLUMNS = [
	{
		id: 'term',
		displayName: 'Searched term'
	},
	{
		id: 'voice_id',
		displayName: 'Voice id'
	},
	{
		id: 'voice_name',
		displayName: 'Voice name'
	},
	{
		id: 'type',
		displayName: 'Type'
	},
	{
		id: 'country',
		displayName: 'Country'
	},
	{
		id: 'social',
		displayName: 'Social'
	},
	{
		id: 'online',
		displayName: 'Online'
	},
	{
		id: 'print',
		displayName: 'Print'
	},
	{
		id: 'urls',
		displayName: 'Urls'
	}
];

function getFoundFragmentHeader(result) {
	let extraText = '';
	if (result.voices.length < result.total) {
		const showingTopText = `Showing top ${result.voices.length}.`;
		extraText = (
			<>
				<>{showingTopText}</>
				<>{showFullLink(showingTopText, result.term)}</>
			</>
		);
	}

	const isFoundTerm = result.total !== 0;
	let cardSubtitle;

	if (isFoundTerm) {
		cardSubtitle = (
			<h6 className="card-subtitle text-muted">
				Total results: {result.total}. {extraText}
			</h6>
		);
	}

	return (
		<>
			<Card.Title className={!isFoundTerm ? "term-not-found" : ""}>
				Search term: {result.term}
			</Card.Title>
			{cardSubtitle}
		</>
	);
}

function getFoundFragment(result, setMergeItems) {
	return (
		<AdvancedTable
			key={result.term}
			columns={TABLE_COLUMNS}
			data={processResponse(result)}
			hidePagination={true}
			selectable={true}
			onSelectRows={selectedRows => {
				setMergeItems(prevState => {
					return {
						...prevState,
						[result.term]: selectedRows,
					}
				});
			}}
		>
			{getFoundFragmentHeader(result)}
		</AdvancedTable>
	);
}

function getNotFoundFragment(result) {
	return (
		<Card key={`term_search_${result.term}`}>
			<Card.Header>
				<Card.Title className={"term-not-found"}>
					Search term: {result.term}
				</Card.Title>
			</Card.Header>
		</Card>
	);
}

function showFullLink(show, term) {
	return (show && <> [ <a href={`/voices/search?query=${term}`} target="_blank" rel="noopener noreferrer">Show full</a> ]</>);
}

function countSummary(results) {
	return results.reduce((acc, item) => {
		acc.total++;
		if (item.total > 0) {
			acc.found++;
		} else {
			acc.notFound++;
		}

		return acc;
	}, { total: 0, found: 0, notFound: 0 });
}

function VoiceMSearchResult({ results, maxSearchResultSize, loading, currentPage, totalPages, totalTerms, onPageChange, allTerms, batchSize }) {
	const [processing, setProcessing] = useState(false);
	const [mergeItems, setMergeItems] = useState([]);
	const notFoundRef = useRef();
	const foundTerms = [];
	const notFoundTerms = [];

	const history = useHistory();
	const itemsSelected = Object.values(mergeItems).flat();
	const isProcessable = itemsSelected.length > 1 && itemsSelected.length <= 5;

	useEffect(() => {
		setMergeItems([]);
	}, [currentPage]);

	results.forEach(result => {
		(result.total !== 0 ? foundTerms.push(getFoundFragment(result, setMergeItems)) : notFoundTerms.push(getNotFoundFragment(result)));
	});

	const retrieveResultsToCsv = async () => {
		setProcessing(true);
		const k2Api = new K2Api();

		const parseProfiles = (groups) => {
			const list = Array.isArray(groups) ? groups : Object.values(groups).flat();
			if (!list.length) return '';
			return list.map(o => { return o.url || o.profile_url || o.name }).join(" | ");
		}

		const allData = [];
		for (let i = 0; i < allTerms.length; i += batchSize) {
			const batchTerms = allTerms.slice(i, i + batchSize);
			const k2ApiResponse = await k2Api.post('voices/msearch', { terms: batchTerms, size: maxSearchResultSize });
			allData.push(...k2ApiResponse.data);
		}

		const result = allData.flatMap(searchedTerm => {
			if (!searchedTerm.voices.length) return { term: searchedTerm.term };
			return searchedTerm.voices.map(voice => {
				return {
					id: voice.id,
					term: searchedTerm.term,
					voice_id: voice.id,
					voice_name: voice.name,
					type: voice.type,
					country: voice.country ? voice.country.name : '',
					social: parseProfiles(voice.socialmedia),
					online: parseProfiles(voice.medias.online),
					print: parseProfiles(voice.medias.print),
					urls: parseProfiles(voice.urls)
				}
			})
		});

		setProcessing(false);
		return result;
	}

	const { found, notFound } = countSummary(results);

	const onMergeClick = () => {
		const voices = results.map(result => result.voices).flat();
		const selectedVoices = itemsSelected.map(id => voices.find(voice => voice.id === id));
		history.push({
			pathname: "/voices/merge",
			search: `?ids=${selectedVoices.map(voice => voice.id).join(',')}`,
			state: {
				voices: selectedVoices
			}
		});
	};

	const batchStart = (currentPage - 1) * batchSize + 1;
	const batchEnd = Math.min(currentPage * batchSize, totalTerms);

	return (
		<>
			<div className="voice-search-results">
				<h1>Voice Mass Search - Results</h1>
				<div className="d-flex flex-row justify-content-between">
					<h4 className="page-subtitle">
						<span>Voices searched: {totalTerms}</span>
						{totalPages > 1 && <span> | Showing {batchStart}-{batchEnd}</span>}
						{!loading && (
							<>
								<span> | Found: {found}</span>
								<span className={notFoundTerms.length > 0 ? "not-found-link" : null} onClick={() => notFoundRef.current?.scrollIntoView({ behavior: 'smooth' })}> | Not found: {notFound}</span>
							</>
						)}
					</h4>
					<div>
						{processing ? <Loader /> : null}
						<Button variant="warning" className="float-left mb-3" disabled={!isProcessable} onClick={() => onMergeClick()}>Merge Voices</Button>
						<ExportCSV columns={CSV_COLUMNS} disabled={processing} datas={retrieveResultsToCsv} filename="VoiceMassSearchReport" buttonText="Download results in CSV" />
					</div>
				</div>
				<div>
					{itemsSelected.length > 5 ? <Alert variant="danger">You can only merge up to 5 voices at a time</Alert> : null}
				</div>
				{loading ? (
					<div className="d-flex justify-content-center my-5">
						<Loader size={40} />
					</div>
				) : (
					<>
						<div>{foundTerms}</div>
						<div ref={notFoundRef} id="not_found_terms">{notFoundTerms}</div>
					</>
				)}
				{totalPages > 1 && (
					<div className="d-flex flex-row justify-content-end align-items-center mt-3 gap-3 msearch-pagination">
						{loading && <Loader size={40} />}
						<TablePagination
							onRowsPerPageChange={() => {}}
							onPageChange={onPageChange}
							count={totalTerms}
							rowsPerPage={batchSize}
							page={currentPage}
						/>
					</div>
				)}
			</div>
			<GoTopButton />
		</>
	);
}

VoiceMSearchResult.propTypes = {
	results: PropTypes.arrayOf(
		PropTypes.shape({
			term: PropTypes.string.isRequired,
			total: PropTypes.number.isRequired,
			voices: PropTypes.array.isRequired
		})
	).isRequired,
	maxSearchResultSize: PropTypes.number.isRequired,
	loading: PropTypes.bool.isRequired,
	currentPage: PropTypes.number.isRequired,
	totalPages: PropTypes.number.isRequired,
	totalTerms: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	allTerms: PropTypes.arrayOf(PropTypes.string).isRequired,
	batchSize: PropTypes.number.isRequired
}

export default VoiceMSearchResult;
